import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { getTranslation } from '../utils/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All original hero images preserved (LANDSCAPE — desktop / tablet / landscape)
const ALL_HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/rpyjto79_quentin-martinez-puYeN_pxPQk-unsplash.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/nnomeu1t_david-leveque-esvWH-owWug-unsplash.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/rdqadans_3735.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/quzfrp4g_FB_IMG_1770695066393.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/mimo9y8q_Shelby%20Gt500%20Eleanor%20M%20Sculo%20Car%20Photo%20HD%20Wallpaper.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/ccrqp8nh_FB_IMG_1770695261526.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/ijz6fvay_FB_IMG_1770696143613.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/y0nbscjj_quentin-martinez-OrFnKpqi_b4-unsplash.jpg"
];

// PORTRAIT hero images — art-directed crops for mobile portrait (9:16 ratio)
// PLACEHOLDER: Replace each URL with the final portrait-cropped version once approved
const PORTRAIT_HERO_IMAGES = [
  null, // 0: Diablo GT — portrait crop pending
  null, // 1: BMW M4 — portrait crop pending
  null, // 2: Chiron — portrait crop pending
  null, // 3: Ferrari 250 — portrait crop pending
  null, // 4: Eleanor — portrait crop pending
  null, // 5: Countach — portrait crop pending
  null, // 6: Senna — portrait crop pending
  null, // 7: F40 — portrait crop pending
];

// Semantic groupings — images categorized by meaning
// Group 0: Movement — Bugatti Chiron (forward energy), BMW M4 (dynamic tire marks)
// Group 1: Internal — Diablo GT (quiet showroom), McLaren Senna (doors open, intimate)
// Group 2: Continuation — Classic Ferrari 250 (heritage), Shelby Eleanor (cinematic lineage)
// Group 3: Custodianship — Countach (archival, preserved), F40 (composed stewardship)
const HERO_GROUPS = [
  [2, 1],  // Movement
  [0, 6],  // Internal
  [3, 4],  // Continuation
  [5, 7],  // Custodianship
];

// Flat slide sequence: each entry knows its image and which manifesto group it belongs to
const SLIDE_SEQUENCE = HERO_GROUPS.flatMap((group, groupIdx) =>
  group.map(imgIdx => ({ imgIdx, groupIdx }))
);

const Home = () => {
  useDocumentMeta({
    title: 'Revs Vibes | Advisory, Editorial, and Collector Car Culture',
    description: 'Revs Vibes is a modern platform rooted in automotive heritage, bringing together advisory, editorial, and collector car culture through design, movement, and lasting quality.',
    canonical: 'https://www.revsvibes.com/',
    image: 'https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/rdqadans_3735.jpg',
  });
  const [currentPos, setCurrentPos] = useState(0);
  const [activeLayer, setActiveLayer] = useState(0);
  const [layerAIdx, setLayerAIdx] = useState(SLIDE_SEQUENCE[0].imgIdx);
  const [layerBIdx, setLayerBIdx] = useState(SLIDE_SEQUENCE[0].imgIdx);
  const [heroAnimDone, setHeroAnimDone] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const { language } = useLanguage();
  const layerRef = useRef(0);

  useEffect(() => {
    fetch(`${API_URL}/api/blog`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setBlogPosts(data))
      .catch(() => setBlogPosts([]));
    const animTimer = setTimeout(() => setHeroAnimDone(true), 3200);
    return () => clearTimeout(animTimer);
  }, []);

  // Manifesto lines in correct sequence
  const manifestoMap = {
    en: ['We begin with movement.','The real engine is internal.','Each car carries something forward.','Custodians, not collectors.'],
    es: ['Comenzamos con el movimiento.','El verdadero motor es interno.','Cada coche lleva algo adelante.','Custodios, no coleccionistas.'],
    pt: ['Começamos com movimento.','O verdadeiro motor é interno.','Cada carro leva algo adiante.','Guardadores, não colecionadores.'],
    fr: ['Nous commençons par le mouvement.','Le véritable moteur est intérieur.','Chaque voiture porte quelque chose en avant.','Gardiens, pas collectionneurs.'],
    it: ['Iniziamo con il movimento.','Il vero motore è interno.','Ogni auto porta qualcosa avanti.','Custodi, non collezionisti.'],
  };
  const manifestoLines = manifestoMap[language] || manifestoMap.en;

  // Which manifesto line is active (derived from currentPos)
  const currentManifestoIdx = SLIDE_SEQUENCE[currentPos].groupIdx;

  // Unified rotation — images cycle within a group, then group advances
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPos(prev => {
        const next = (prev + 1) % SLIDE_SEQUENCE.length;
        const nextImgIdx = SLIDE_SEQUENCE[next].imgIdx;
        if (layerRef.current === 0) {
          setLayerBIdx(nextImgIdx);
          requestAnimationFrame(() => {
            setActiveLayer(1);
            layerRef.current = 1;
          });
        } else {
          setLayerAIdx(nextImgIdx);
          requestAnimationFrame(() => {
            setActiveLayer(0);
            layerRef.current = 0;
          });
        }
        return next;
      });
    }, 6500);
    return () => clearInterval(interval);
  }, []);

  const heroText = {
    en: {
      h1: 'The cars are a continuation, not the beginning.',
      sub: 'We are only passing through as custodians.',
      line1: 'The work is quiet and deliberate.',
      line2: 'Each car, placed where it belongs.',
    },
    es: {
      h1: 'Los autos son una continuación, no el comienzo.',
      sub: 'Solo estamos de paso como custodios.',
      line1: 'El trabajo es silencioso y deliberado.',
      line2: 'Cada auto, colocado donde pertenece.',
    },
    pt: {
      h1: 'Os carros são uma continuação, não o começo.',
      sub: 'Estamos apenas de passagem como guardiões.',
      line1: 'O trabalho é silencioso e deliberado.',
      line2: 'Cada carro, colocado onde pertence.',
    },
    fr: {
      h1: 'Les voitures sont une continuation, pas le commencement.',
      sub: 'Nous ne faisons que passer, comme dépositaires.',
      line1: 'Le travail est silencieux et délibéré.',
      line2: 'Chaque voiture, placée là où elle appartient.',
    },
    it: {
      h1: "Le auto sono una continuazione, non l'inizio.",
      sub: 'Siamo solo di passaggio come custodi.',
      line1: 'Il lavoro è silenzioso e deliberato.',
      line2: 'Ogni auto, collocata dove appartiene.',
    }
  };
  const ht = heroText[language] || heroText.en;

  const brandText = {
    en: {
      h2: 'Movement carries meaning.',
      body: 'We move with intent. The rest follows.',
    },
    es: {
      h2: 'El movimiento lleva significado.',
      body: 'Nos movemos con intención. El resto sigue.',
    },
    pt: {
      h2: 'O movimento carrega significado.',
      body: 'Nos movemos com intenção. O resto segue.',
    },
    fr: {
      h2: 'Le mouvement porte du sens.',
      body: 'Nous avançons avec intention. Le reste suit.',
    },
    it: {
      h2: 'Il movimento porta significato.',
      body: 'Ci muoviamo con intenzione. Il resto segue.',
    }
  };
  const bt = brandText[language] || brandText.en;

  const advisoryText = {
    en: 'We are present where alignment matters. Privately. Independently.',
    es: 'Estamos presentes donde la alineación importa. En privado. De forma independiente.',
    pt: 'Estamos presentes onde o alinhamento importa. Em privado. De forma independente.',
    fr: "Nous sommes présents là où l'alignement compte. En privé. De manière indépendante.",
    it: "Siamo presenti dove l'allineamento conta. In privato. In modo indipendente.",
  };

  const talesText = {
    en: {
      h2: 'Stories worth carrying forward.',
      body: 'What a car has lived does not end with a change of hands. It continues. That is the point.',
    },
    es: {
      h2: 'Historias que vale la pena llevar adelante.',
      body: 'Lo que un auto ha vivido no termina con un cambio de manos. Continúa. Ese es el punto.',
    },
    pt: {
      h2: 'Histórias que vale a pena levar adiante.',
      body: 'O que um carro viveu não termina com uma troca de mãos. Continua. Esse é o ponto.',
    },
    fr: {
      h2: "Des histoires qui méritent d'être portées.",
      body: "Ce qu'une voiture a vécu ne s'arrête pas avec un changement de mains. Cela continue. C'est là l'essentiel.",
    },
    it: {
      h2: 'Storie che meritano di essere portate avanti.',
      body: "Ciò che un'auto ha vissuto non finisce con un cambio di mani. Continua. Questo è il punto.",
    }
  };
  const tt = talesText[language] || talesText.en;

  return (
    <div className="min-h-screen">
      {/* Hero — seamless crossfade carousel */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        {/* Layer A — always mounted */}
        <div className="hero-slide absolute inset-0" style={{ opacity: activeLayer === 0 ? 1 : 0, transition: 'opacity 1.1s cubic-bezier(0.4, 0, 0.2, 1)', zIndex: 0 }}>
          <picture>
            {PORTRAIT_HERO_IMAGES[layerAIdx] && (
              <source
                media="(max-width: 768px) and (orientation: portrait)"
                srcSet={PORTRAIT_HERO_IMAGES[layerAIdx]}
              />
            )}
            <img src={ALL_HERO_IMAGES[layerAIdx]} alt="" className="w-full h-full object-cover" />
          </picture>
        </div>
        {/* Layer B — always mounted */}
        <div className="hero-slide absolute inset-0" style={{ opacity: activeLayer === 1 ? 1 : 0, transition: 'opacity 1.1s cubic-bezier(0.4, 0, 0.2, 1)', zIndex: 1 }}>
          <picture>
            {PORTRAIT_HERO_IMAGES[layerBIdx] && (
              <source
                media="(max-width: 768px) and (orientation: portrait)"
                srcSet={PORTRAIT_HERO_IMAGES[layerBIdx]}
              />
            )}
            <img src={ALL_HERO_IMAGES[layerBIdx]} alt="" className="w-full h-full object-cover" />
          </picture>
        </div>
        {/* Persistent overlay — gradient for consistent readability */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.68) 100%)', zIndex: 2 }} />

        {/* Persistent text container — never remounts */}
        <div className="relative min-h-screen flex items-center justify-center text-center px-6 py-24" style={{ zIndex: 3 }}>
          <div style={{ maxWidth: '850px', textShadow: '0 2px 20px rgba(0,0,0,0.35)' }}>
            <h1 className={`${heroAnimDone ? '' : 'hero-fade hero-fade-1'} text-4xl sm:text-5xl md:text-6xl font-semibold text-white mb-8 sm:mb-10 md:mb-16 leading-tight`}>
              {ht.h1}
            </h1>

            <p
              className={`${heroAnimDone ? '' : 'hero-fade hero-fade-2'} mb-12 sm:mb-18 md:mb-24`}
              style={{ fontSize: 'clamp(18px, 2.5vw, 26px)', fontWeight: 400, color: 'rgba(255,255,255,0.85)' }}
            >
              {ht.sub}
            </p>

            <p
              className={`${heroAnimDone ? '' : 'hero-fade hero-fade-3'} mb-1`}
              style={{ fontSize: 'clamp(14px, 1.4vw, 18px)', fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}
            >
              {ht.line1}
            </p>
            <p
              className={heroAnimDone ? '' : 'hero-fade hero-fade-3b'}
              style={{ fontSize: 'clamp(14px, 1.4vw, 18px)', fontWeight: 400, color: 'rgba(255,255,255,0.65)', marginTop: '6px', marginBottom: '36px' }}
            >
              {ht.line2}
            </p>

            {/* Manifesto rotating line — quiet closing thought */}
            <div className={`${heroAnimDone ? '' : 'hero-fade hero-fade-4'} relative h-6`} style={{ marginBottom: '48px' }}>
              {manifestoLines.map((line, idx) => (
                <span
                  key={idx}
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    fontSize: 'clamp(11px, 1.1vw, 13px)',
                    fontWeight: 300,
                    letterSpacing: '0.15em',
                    color: 'rgba(255,255,255,0.40)',
                    opacity: idx === currentManifestoIdx ? 1 : 0,
                    transition: 'opacity 1.1s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {line}
                </span>
              ))}
            </div>

            <div className={`${heroAnimDone ? '' : 'hero-fade hero-fade-5'} flex flex-col sm:flex-row gap-5 justify-center`}>
              <Link to="/services">
                <Button
                  size="lg"
                  className="hero-btn-primary bg-white/20 text-white border-2 border-white px-10 py-5 text-[13px] tracking-[0.08em] rounded-sm"
                  data-testid="hero-enter-advisory-btn"
                >
                  {language === 'en' ? 'Enter the Advisory' :
                   language === 'es' ? 'Entrar a la Asesor\u00eda' :
                   language === 'pt' ? 'Entrar na Consultoria' :
                   language === 'fr' ? 'Entrer dans le Conseil' :
                   'Entra nella Consulenza'}
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  size="lg"
                  variant="outline"
                  className="hero-btn-outline border border-white/60 text-white bg-transparent px-10 py-5 text-[13px] tracking-[0.08em] rounded-sm"
                  data-testid="hero-explore-revs-btn"
                >
                  {language === 'en' ? 'Explore Revs' :
                   language === 'es' ? 'Explorar Revs' :
                   language === 'pt' ? 'Explorar Revs' :
                   language === 'fr' ? 'Explorer Revs' :
                   'Esplora Revs'}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce" style={{ zIndex: 4 }}>
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/40 rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Brand Statement — continuation, not explanation */}
      <section className="relative py-24 sm:py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_32f6a5a4-f0f4-4863-af34-6e0c296f55d4/artifacts/b7h2xbjx_Screenshot%202026-03-18%20192727.png"
            alt="Classic car interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative mx-auto px-6 text-center" style={{ maxWidth: '650px' }}>
          <h2
            className="font-semibold text-white mb-6 whitespace-pre-line"
            style={{ fontSize: 'clamp(28px, 3vw, 42px)', lineHeight: 1.25 }}
          >
            {bt.h2}
          </h2>
          <p className="text-base sm:text-lg text-white/70 leading-relaxed">
            {bt.body}
          </p>
        </div>
      </section>

      {/* Micro-tagline — philosophical pause */}
      <div className="py-24 sm:py-28 bg-white text-center">
        <p className="text-[13px] tracking-[0.28em] font-light" style={{ color: '#6f7780' }}>
          {language === 'en' ? 'The car is not the destination.' :
           language === 'es' ? 'El coche no es el destino.' :
           language === 'pt' ? 'O carro n\u00e3o \u00e9 o destino.' :
           language === 'fr' ? 'La voiture n\u2019est pas la destination.' :
           'L\u2019auto non \u00e8 la destinazione.'}
        </p>
      </div>

      {/* Advisory Positioning — concise, no explanation */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-base sm:text-lg text-gray-600 leading-[1.75] font-light">
            {advisoryText[language] || advisoryText.en}
          </p>
        </div>
      </section>

      {/* Quote Section — integrated, not inserted */}
      <section className="relative py-24 sm:py-32 md:py-40 overflow-hidden min-h-[450px] sm:min-h-[600px] md:min-h-[750px]">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_b3960aa7-330d-4b96-81c0-018bec21e519/artifacts/via6ctq6_Untitled%20%281920%20x%201080%20px%29.png"
            alt="LaFerrari"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative mx-auto px-6 text-center" style={{ maxWidth: '650px' }}>
          <blockquote className="text-2xl sm:text-3xl md:text-4xl font-light italic mb-8 text-white/90 leading-snug">
            "{getTranslation(language, 'quote.text')}"
          </blockquote>
          <cite className="text-white/50 not-italic text-sm tracking-wide">— {getTranslation(language, 'quote.author')}</cite>
        </div>
      </section>

      {/* Micro-tagline — core thesis */}
      <div className="py-20 sm:py-24 bg-white text-center">
        <p className="text-base tracking-[0.16em] font-light" style={{ color: '#6f7780' }}>
          {language === 'en' ? 'What continues matters.' :
           language === 'es' ? 'Lo que contin\u00faa importa.' :
           language === 'pt' ? 'O que continua importa.' :
           language === 'fr' ? 'Ce qui perdure compte.' :
           'Ci\u00f2 che continua conta.'}
        </p>
      </div>

      {/* Revs Tales — editorial, not a grid */}
      <section className="pt-20 md:pt-26 pb-28 md:pb-36 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16 md:mb-20">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-4 text-black">{tt.h2}</h2>
            <p className="text-gray-500 text-base leading-relaxed font-light">
              {tt.body}
            </p>
          </div>

          {/* Editorial Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 mb-16">
            {/* Featured Story */}
            {blogPosts.length > 0 && (
              <Link to={blogPosts[0].slug ? `/tales/${blogPosts[0].slug}` : `/blog/${blogPosts[0].id}`} className="group">
                <div className="overflow-hidden mb-5">
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full aspect-[3/2] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400 uppercase tracking-wider mb-3">
                  <span>{blogPosts[0].category}</span>
                  <span className="w-px h-3 bg-gray-200" />
                  <span>{blogPosts[0].date}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-black mb-3 leading-snug group-hover:text-gray-600 transition-colors duration-300">
                  {blogPosts[0].title}
                </h3>
                <p className="text-gray-500 text-base leading-relaxed mb-4 line-clamp-2">
                  {blogPosts[0].excerpt}
                </p>
                <span className="text-[13px] text-gray-400 font-medium">Read More →</span>
              </Link>
            )}

            {/* Secondary Stories */}
            <div className="flex flex-col gap-12 md:gap-14 justify-between">
              {blogPosts.slice(1, 3).map((post) => (
                <Link key={post.id} to={`/blog/${post.id}`} className="group flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-full sm:w-44 h-48 sm:h-32 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-3 text-xs text-gray-400 uppercase tracking-wider mb-2">
                      <span>{post.category}</span>
                      <span className="w-px h-3 bg-gray-200" />
                      <span>{post.date}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-black mb-2 leading-snug group-hover:text-gray-600 transition-colors duration-300">
                      {post.title}
                    </h3>
                    <span className="text-[13px] text-gray-400 font-medium">Read More →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* View All */}
          <div className="text-center">
            <Link to="/blog">
              <Button variant="outline" className="px-10 py-5 text-[13px] tracking-[0.06em] rounded-sm border-gray-300 hover:border-gray-500 transition-colors duration-300">
                {language === 'en' ? 'View Revs Tales' :
                 language === 'es' ? 'Ver Revs Tales' :
                 language === 'pt' ? 'Ver Revs Tales' :
                 language === 'fr' ? 'Voir Revs Tales' :
                 'Vedi Revs Tales'} <ArrowRight size={16} className="ml-2 inline" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA — single, natural conclusion */}
      <section className="py-28 md:py-36 bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold mb-6">{getTranslation(language, 'cta.title')}</h2>
          <p className="text-base text-gray-400 mb-10 font-light leading-relaxed max-w-xl mx-auto">
            {getTranslation(language, 'cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="hero-btn-primary bg-white/20 text-white border-2 border-white px-10 py-5 text-[13px] tracking-[0.08em] rounded-sm">
                {getTranslation(language, 'cta.button')}
              </Button>
            </Link>
            <Link to="/car-alert">
              <Button size="lg" variant="outline" className="hero-btn-outline border border-white/60 text-white bg-transparent px-10 py-5 text-[13px] tracking-[0.08em] rounded-sm" data-testid="home-car-alert-btn">
                Revs Signal
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
